import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PageShell from "./PageShell";
import "./PortalUI.css";
import {
    createLead,
    deleteLead,
    getLeadDiscoverySettings,
    getLeadMetrics,
    getLeads,
    getOutlookCalendarsForMailbox,
    resendLeadDiscoveryInvite,
    updateLead,
    updateLeadDiscoverySettings,
} from "./api";

const STATUS_OPTIONS = [
    { value: "enquired", label: "Enquired" },
    { value: "discovery_call_scheduled", label: "Discovery Call: Scheduled" },
    { value: "discovery_call_cancelled", label: "Discovery Call: Cancelled" },
    { value: "signed_up", label: "Signed Up" },
    { value: "declined", label: "Declined" },
];

const STATUS_FILTER_OPTIONS = [
    { value: "all_except_declined", label: "All (except Declined)" },
    { value: "", label: "All" },
    ...STATUS_OPTIONS,
];

const PODCAST_TYPE_OPTIONS = [
    { value: "business", label: "Business" },
    { value: "personal", label: "Personal" },
];

const FREQUENCY_OPTIONS = [
    { value: "monthly", label: "Monthly" },
    { value: "fortnightly", label: "Fortnightly" },
    { value: "weekly", label: "Weekly" },
    { value: "not_sure", label: "Not Sure" },
];

const METRIC_RANGE_OPTIONS = [7, 30, 90];

const emptyMetrics = {
    days: 30,
    kpis: {
        invites_sent: 0,
        bookings: 0,
        invite_to_book_pct: 0,
        avg_days_call_to_signed_up: null,
    },
    bookings_by_weekday: [],
    bookings_by_hour: [],
    funnel: {
        enquired: 0,
        discovery_call_scheduled: 0,
        discovery_call_cancelled: 0,
        signed_up: 0,
        declined: 0,
    },
};

const emptyForm = {
    id: null,
    name: "",
    email: "",
    phone: "",
    lead_date: "",
    podcast_type: "",
    frequency: "",
    already_has_podcast: false,
    services: "",
    status: "enquired",
    direct_debit_mandate_url: "",
    onboarding_token: "",
    onboarding_completed_at: null,
    discovery_call_token: "",
    discovery_call_token_expires_at: null,
    discovery_call_scheduled_at: null,
    discovery_call_duration_minutes: 30,
    discovery_call_cancelled_at: null,
    notes: "",
};

const defaultDiscoverySettings = {
    timezone: "Europe/London",
    available_weekdays: [1, 2, 3, 4, 5],
    day_start_time: "09:00",
    day_end_time: "17:00",
    slot_minutes: 30,
    buffer_minutes: 0,
    max_calls_per_day: 6,
    min_notice_hours: 24,
    max_days_ahead: 60,
    outlook_calendar_user_id: "",
    outlook_calendar_id: "",
    booking_redirect_url: "",
    booking_redirect_include_params: false,
};

function formatStatus(value) {
    if (value === "discovery_call") return "Discovery Call: Scheduled";
    return (
        STATUS_OPTIONS.find((option) => option.value === value)?.label ||
        "Unknown"
    );
}

function getLeadStatusPillClass(value) {
    const normalized = String(value || "").toLowerCase().trim();
    if (normalized === "discovery_call_cancelled") return "pill--lead-cancelled";
    if (normalized === "discovery_call_scheduled" || normalized === "discovery_call") {
        return "pill--lead-scheduled";
    }
    return "pill--role-admin";
}

function formatPodcastType(value) {
    return (
        PODCAST_TYPE_OPTIONS.find((option) => option.value === value)?.label ||
        "Not set"
    );
}

function formatFrequency(value) {
    return (
        FREQUENCY_OPTIONS.find((option) => option.value === value)?.label ||
        "Not set"
    );
}

function formatLeadDate(value) {
    if (!value) return "Not set";
    const parsed = new Date(`${String(value).slice(0, 10)}T00:00:00Z`);
    if (Number.isNaN(parsed.getTime())) return String(value);
    return parsed.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        timeZone: "UTC",
    });
}

function formatMetricValue(value) {
    if (value === null || value === undefined) return "—";
    return Number(value).toLocaleString("en-GB");
}

function maxChartValue(series) {
    const maxValue = Math.max(...(series || []).map((row) => Number(row.count || 0)), 0);
    return maxValue <= 0 ? 1 : maxValue;
}

function Leads({ onLogout, embedded = false }) {
    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("all_except_declined");
    const [podcastTypeFilter, setPodcastTypeFilter] = useState("");
    const [frequencyFilter, setFrequencyFilter] = useState("");
    const [alreadyHasPodcastFilter, setAlreadyHasPodcastFilter] = useState("");

    const [modalOpen, setModalOpen] = useState(false);
    const [form, setForm] = useState(emptyForm);
    const [initialFormSnapshot, setInitialFormSnapshot] = useState(emptyForm);
    const [formError, setFormError] = useState("");
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [copiedLink, setCopiedLink] = useState(false);
    const [discoveryCopiedLink, setDiscoveryCopiedLink] = useState(false);
    const formRef = useRef(null);

    const hasUnsavedChanges =
        JSON.stringify(form) !== JSON.stringify(initialFormSnapshot);

    const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
    const [settingsForm, setSettingsForm] = useState(defaultDiscoverySettings);
    const [settingsLoading, setSettingsLoading] = useState(false);
    const [settingsSaving, setSettingsSaving] = useState(false);
    const [settingsError, setSettingsError] = useState("");
    const [calendarOptions, setCalendarOptions] = useState([]);
    const [calendarsLoading, setCalendarsLoading] = useState(false);
    const [showMetrics, setShowMetrics] = useState(false);
    const [metricsRangeDays, setMetricsRangeDays] = useState(30);
    const [metrics, setMetrics] = useState(emptyMetrics);
    const [metricsLoading, setMetricsLoading] = useState(false);
    const [metricsError, setMetricsError] = useState("");

    const loadLeads = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const rows = await getLeads({
                search,
                status: statusFilter,
                podcast_type: podcastTypeFilter,
                frequency: frequencyFilter,
                already_has_podcast: alreadyHasPodcastFilter,
            });
            setLeads(rows);
        } catch (err) {
            console.error(err);
            setError(err.message || "Failed to load leads");
        } finally {
            setLoading(false);
        }
    }, [search, statusFilter, podcastTypeFilter, frequencyFilter, alreadyHasPodcastFilter]);

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            loadLeads();
        }, 150);
        return () => clearTimeout(timeoutId);
    }, [loadLeads]);

    const loadMetrics = useCallback(async () => {
        setMetricsLoading(true);
        setMetricsError("");
        try {
            const data = await getLeadMetrics(metricsRangeDays);
            setMetrics({
                ...emptyMetrics,
                ...data,
                kpis: {
                    ...emptyMetrics.kpis,
                    ...(data?.kpis || {}),
                },
                funnel: {
                    ...emptyMetrics.funnel,
                    ...(data?.funnel || {}),
                },
            });
        } catch (err) {
            console.error(err);
            setMetricsError(err.message || "Failed to load lead metrics");
        } finally {
            setMetricsLoading(false);
        }
    }, [metricsRangeDays]);

    useEffect(() => {
        if (!showMetrics) return;
        loadMetrics();
    }, [showMetrics, loadMetrics]);

    const titleCount = useMemo(() => leads.length, [leads]);

    function openCreateModal() {
        const fresh = { ...emptyForm };
        setForm(fresh);
        setInitialFormSnapshot(fresh);
        setFormError("");
        setCopiedLink(false);
        setDiscoveryCopiedLink(false);
        setModalOpen(true);
    }

    function openEditModal(lead) {
        const nextForm = {
            id: lead.id,
            name: lead.name || "",
            email: lead.email || "",
            phone: lead.phone || "",
            lead_date: lead.lead_date || "",
            podcast_type: lead.podcast_type || "",
            frequency: lead.frequency || "",
            already_has_podcast: Boolean(lead.already_has_podcast),
            services: lead.services || "",
            status: lead.status || "enquired",
            direct_debit_mandate_url: lead.direct_debit_mandate_url || "",
            onboarding_token: lead.onboarding_token || "",
            onboarding_completed_at: lead.onboarding_completed_at || null,
            discovery_call_token: lead.discovery_call_token || "",
            discovery_call_token_expires_at:
                lead.discovery_call_token_expires_at || null,
            discovery_call_scheduled_at: lead.discovery_call_scheduled_at || null,
            discovery_call_duration_minutes:
                lead.discovery_call_duration_minutes || 30,
            discovery_call_cancelled_at: lead.discovery_call_cancelled_at || null,
            notes: lead.notes || "",
        };
        setForm(nextForm);
        setInitialFormSnapshot(nextForm);
        setFormError("");
        setCopiedLink(false);
        setDiscoveryCopiedLink(false);
        setModalOpen(true);
    }

    function closeModal() {
        setModalOpen(false);
        setForm(emptyForm);
        setInitialFormSnapshot(emptyForm);
        setFormError("");
        setCopiedLink(false);
        setDiscoveryCopiedLink(false);
    }

    function requestCloseModal() {
        if (hasUnsavedChanges) {
            const ok = window.confirm(
                "You have unsaved changes. Close without saving?"
            );
            if (!ok) return;
        }
        closeModal();
    }

    async function handleSave(e) {
        e.preventDefault();
        setSaving(true);
        setFormError("");

        try {
            const payload = {
                name: form.name,
                email: form.email || null,
                phone: form.phone || null,
                lead_date: form.lead_date || null,
                podcast_type: form.podcast_type || null,
                frequency: form.frequency || null,
                already_has_podcast: Boolean(form.already_has_podcast),
                services: form.services || null,
                status: form.status || "enquired",
                direct_debit_mandate_url: form.direct_debit_mandate_url || null,
                notes: form.notes || null,
            };

            if (form.id) {
                const saved = await updateLead(form.id, payload);
                setLeads((prev) =>
                    prev.map((row) => (row.id === saved.id ? saved : row))
                );
                setForm((prev) => ({
                    ...prev,
                    onboarding_token: saved.onboarding_token || "",
                    onboarding_completed_at: saved.onboarding_completed_at || null,
                    discovery_call_token: saved.discovery_call_token || "",
                    discovery_call_token_expires_at:
                        saved.discovery_call_token_expires_at || null,
                    discovery_call_scheduled_at:
                        saved.discovery_call_scheduled_at || null,
                    discovery_call_duration_minutes:
                        saved.discovery_call_duration_minutes || 30,
                    discovery_call_cancelled_at:
                        saved.discovery_call_cancelled_at || null,
                }));
            } else {
                const saved = await createLead(payload);
                setLeads((prev) => [saved, ...prev]);
                setForm((prev) => ({
                    ...prev,
                    id: saved.id,
                    onboarding_token: saved.onboarding_token || "",
                    onboarding_completed_at: saved.onboarding_completed_at || null,
                    discovery_call_token: saved.discovery_call_token || "",
                    discovery_call_token_expires_at:
                        saved.discovery_call_token_expires_at || null,
                    discovery_call_scheduled_at:
                        saved.discovery_call_scheduled_at || null,
                    discovery_call_duration_minutes:
                        saved.discovery_call_duration_minutes || 30,
                    discovery_call_cancelled_at:
                        saved.discovery_call_cancelled_at || null,
                }));
            }

            closeModal();
        } catch (err) {
            console.error(err);
            setFormError(err.message || "Failed to save lead");
        } finally {
            setSaving(false);
        }
    }

    async function handleDelete() {
        if (!form.id) return;
        if (!window.confirm("Delete this lead?")) return;

        setDeleting(true);
        setFormError("");
        try {
            await deleteLead(form.id);
            setLeads((prev) => prev.filter((row) => row.id !== form.id));
            closeModal();
        } catch (err) {
            console.error(err);
            setFormError(err.message || "Failed to delete lead");
        } finally {
            setDeleting(false);
        }
    }

    async function copyOnboardingLink() {
        if (!form.onboarding_token) return;

        const link = `${window.location.origin}/#/onboarding?token=${encodeURIComponent(form.onboarding_token)}`;
        try {
            await navigator.clipboard.writeText(link);
            setCopiedLink(true);
            setTimeout(() => setCopiedLink(false), 2000);
        } catch (err) {
            console.error("Failed to copy onboarding link:", err);
            setFormError("Failed to copy onboarding link");
        }
    }

    async function copyDiscoveryLink() {
        if (!form.discovery_call_token) return;

        const link = `${window.location.origin}/#/discovery-call?token=${encodeURIComponent(form.discovery_call_token)}`;
        try {
            await navigator.clipboard.writeText(link);
            setDiscoveryCopiedLink(true);
            setTimeout(() => setDiscoveryCopiedLink(false), 2000);
        } catch (err) {
            console.error("Failed to copy discovery link:", err);
            setFormError("Failed to copy discovery link");
        }
    }

    async function handleResendDiscoveryInvite() {
        if (!form.id) return;
        try {
            await resendLeadDiscoveryInvite(form.id);
        } catch (err) {
            console.error(err);
            setFormError(err.message || "Failed to resend discovery call invite");
        }
    }

    async function openSettingsModal() {
        setIsSettingsModalOpen(true);
        setSettingsLoading(true);
        setSettingsError("");
        setCalendarOptions([]);

        try {
            const settings = await getLeadDiscoverySettings();
            const merged = {
                ...defaultDiscoverySettings,
                ...settings,
                available_weekdays:
                    settings?.available_weekdays || defaultDiscoverySettings.available_weekdays,
            };
            setSettingsForm(merged);

            if (merged.outlook_calendar_user_id) {
                try {
                    setCalendarsLoading(true);
                    const calendars = await getOutlookCalendarsForMailbox(
                        merged.outlook_calendar_user_id
                    );
                    setCalendarOptions(calendars);
                } catch (err) {
                    console.error(err);
                    setSettingsError(
                        err.message || "Failed to load Outlook calendars"
                    );
                } finally {
                    setCalendarsLoading(false);
                }
            }
        } catch (err) {
            console.error(err);
            setSettingsError(err.message || "Failed to load discovery settings");
        } finally {
            setSettingsLoading(false);
        }
    }

    async function handleLoadCalendars() {
        const mailbox = String(settingsForm.outlook_calendar_user_id || "").trim();
        if (!mailbox) {
            setSettingsError("Enter Outlook mailbox/user first");
            return;
        }

        setCalendarsLoading(true);
        setSettingsError("");
        try {
            const calendars = await getOutlookCalendarsForMailbox(mailbox);
            setCalendarOptions(calendars);
            if (!calendars.find((c) => c.id === settingsForm.outlook_calendar_id)) {
                setSettingsForm((prev) => ({
                    ...prev,
                    outlook_calendar_id: calendars[0]?.id || "",
                }));
            }
        } catch (err) {
            console.error(err);
            setSettingsError(err.message || "Failed to load Outlook calendars");
        } finally {
            setCalendarsLoading(false);
        }
    }

    async function handleSaveSettings(e) {
        e.preventDefault();
        setSettingsSaving(true);
        setSettingsError("");
        try {
            const saved = await updateLeadDiscoverySettings(settingsForm);
            setSettingsForm({
                ...defaultDiscoverySettings,
                ...saved,
            });
            setIsSettingsModalOpen(false);
        } catch (err) {
            console.error(err);
            setSettingsError(err.message || "Failed to save discovery settings");
        } finally {
            setSettingsSaving(false);
        }
    }

    function toggleWeekday(day) {
        setSettingsForm((prev) => {
            const set = new Set(prev.available_weekdays || []);
            if (set.has(day)) {
                set.delete(day);
            } else {
                set.add(day);
            }
            return {
                ...prev,
                available_weekdays: [...set].sort((a, b) => a - b),
            };
        });
    }

    useEffect(() => {
        if (!modalOpen && !isSettingsModalOpen) return undefined;

        const handleKeyDown = (e) => {
            if (isSettingsModalOpen && e.key === "Escape") {
                e.preventDefault();
                setIsSettingsModalOpen(false);
                return;
            }

            if (e.key === "Escape") {
                e.preventDefault();
                requestCloseModal();
                return;
            }

            const isSaveShortcut =
                (e.key === "s" || e.key === "S") &&
                (e.ctrlKey || e.metaKey);

            if (isSaveShortcut) {
                e.preventDefault();
                if (isSettingsModalOpen) return;
                if (!saving && formRef.current) {
                    if (formRef.current.requestSubmit) {
                        formRef.current.requestSubmit();
                    } else {
                        formRef.current.submit();
                    }
                }
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [modalOpen, isSettingsModalOpen, saving, requestCloseModal, hasUnsavedChanges]);

    return (
        <PageShell embedded={embedded} onLogout={onLogout}>
            <div className="portal-page">
                <div className="portal-page__header">
                    <div>
                        <h2 className="portal-page__title">Leads</h2>
                        <p className="portal-page__subtitle">
                            Admin pipeline for enquiries and discovery calls.
                        </p>
                    </div>

                    <div style={{ display: "flex", gap: 10 }}>
                        <button
                            className="btn btn--secondary"
                            onClick={() => setShowMetrics((prev) => !prev)}
                        >
                            {showMetrics ? "Hide Metrics" : "Show Metrics"}
                        </button>
                        <button className="btn btn--secondary" onClick={openSettingsModal}>
                            Leads Settings
                        </button>
                        <button className="btn btn--primary" onClick={openCreateModal}>
                            + Add Lead
                        </button>
                    </div>
                </div>

                {showMetrics && (
                    <div className="card leads-metrics">
                        <div className="leads-metrics__header">
                            <div>
                                <h3 className="card__title">Discovery Metrics</h3>
                                <p className="card__subtitle">
                                    Booking funnel and timing across the last {metricsRangeDays} days.
                                </p>
                            </div>
                            <label className="form-field" style={{ minWidth: 120, margin: 0 }}>
                                <span className="form-field__label">Range</span>
                                <select
                                    className="form-field__select"
                                    value={metricsRangeDays}
                                    onChange={(e) => setMetricsRangeDays(Number(e.target.value))}
                                >
                                    {METRIC_RANGE_OPTIONS.map((days) => (
                                        <option key={days} value={days}>
                                            Last {days} days
                                        </option>
                                    ))}
                                </select>
                            </label>
                        </div>

                        {metricsError && <div className="alert alert--error">{metricsError}</div>}
                        {metricsLoading ? (
                            <p>Loading metrics…</p>
                        ) : (
                            <>
                                <div className="leads-metrics__kpis">
                                    <div className="leads-metrics__kpi-card">
                                        <span className="leads-metrics__kpi-label">Invites Sent</span>
                                        <span className="leads-metrics__kpi-value">
                                            {formatMetricValue(metrics.kpis.invites_sent)}
                                        </span>
                                    </div>
                                    <div className="leads-metrics__kpi-card">
                                        <span className="leads-metrics__kpi-label">Bookings</span>
                                        <span className="leads-metrics__kpi-value">
                                            {formatMetricValue(metrics.kpis.bookings)}
                                        </span>
                                    </div>
                                    <div className="leads-metrics__kpi-card">
                                        <span className="leads-metrics__kpi-label">
                                            Invite to Book
                                        </span>
                                        <span className="leads-metrics__kpi-value">
                                            {formatMetricValue(metrics.kpis.invite_to_book_pct)}%
                                        </span>
                                    </div>
                                    <div className="leads-metrics__kpi-card">
                                        <span className="leads-metrics__kpi-label">
                                            Avg Days Call to Sign Up
                                        </span>
                                        <span className="leads-metrics__kpi-value">
                                            {metrics.kpis.avg_days_call_to_signed_up === null
                                                ? "—"
                                                : `${formatMetricValue(metrics.kpis.avg_days_call_to_signed_up)}d`}
                                        </span>
                                    </div>
                                </div>

                                <div className="leads-metrics__charts">
                                    <div className="leads-metrics__chart">
                                        <h4 className="leads-metrics__chart-title">
                                            Bookings by Weekday
                                        </h4>
                                        <div className="leads-metrics__bars">
                                            {(metrics.bookings_by_weekday || []).map((row) => (
                                                <div key={row.label} className="leads-metrics__bar-row">
                                                    <span className="leads-metrics__bar-label">
                                                        {row.label}
                                                    </span>
                                                    <div className="leads-metrics__bar-track">
                                                        <div
                                                            className="leads-metrics__bar-fill"
                                                            style={{
                                                                width: `${Math.round(
                                                                    (Number(row.count || 0) /
                                                                        maxChartValue(
                                                                            metrics.bookings_by_weekday
                                                                        )) *
                                                                        100
                                                                )}%`,
                                                            }}
                                                        />
                                                    </div>
                                                    <span className="leads-metrics__bar-count">
                                                        {row.count || 0}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="leads-metrics__chart">
                                        <h4 className="leads-metrics__chart-title">
                                            Bookings by Hour (London)
                                        </h4>
                                        <div className="leads-metrics__bars leads-metrics__bars--dense">
                                            {(metrics.bookings_by_hour || []).map((row) => (
                                                <div key={row.label} className="leads-metrics__bar-row">
                                                    <span className="leads-metrics__bar-label">
                                                        {row.label}
                                                    </span>
                                                    <div className="leads-metrics__bar-track">
                                                        <div
                                                            className="leads-metrics__bar-fill leads-metrics__bar-fill--hour"
                                                            style={{
                                                                width: `${Math.round(
                                                                    (Number(row.count || 0) /
                                                                        maxChartValue(
                                                                            metrics.bookings_by_hour
                                                                        )) *
                                                                        100
                                                                )}%`,
                                                            }}
                                                        />
                                                    </div>
                                                    <span className="leads-metrics__bar-count">
                                                        {row.count || 0}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="leads-metrics__chart">
                                        <h4 className="leads-metrics__chart-title">Status Funnel</h4>
                                        <div className="leads-metrics__funnel">
                                            <div className="leads-metrics__funnel-item">
                                                <span>Enquired</span>
                                                <strong>
                                                    {formatMetricValue(metrics.funnel.enquired)}
                                                </strong>
                                            </div>
                                            <div className="leads-metrics__funnel-item">
                                                <span>Discovery Call: Scheduled</span>
                                                <strong>
                                                    {formatMetricValue(
                                                        metrics.funnel.discovery_call_scheduled
                                                    )}
                                                </strong>
                                            </div>
                                            <div className="leads-metrics__funnel-item">
                                                <span>Discovery Call: Cancelled</span>
                                                <strong>
                                                    {formatMetricValue(
                                                        metrics.funnel.discovery_call_cancelled
                                                    )}
                                                </strong>
                                            </div>
                                            <div className="leads-metrics__funnel-item">
                                                <span>Signed Up</span>
                                                <strong>
                                                    {formatMetricValue(metrics.funnel.signed_up)}
                                                </strong>
                                            </div>
                                            <div className="leads-metrics__funnel-item">
                                                <span>Declined</span>
                                                <strong>
                                                    {formatMetricValue(metrics.funnel.declined)}
                                                </strong>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                )}

                <div className="portal-filter-bar">
                    <div className="portal-filter-bar__group portal-filter-bar__group--grow">
                        <span className="portal-filter-bar__label">Search</span>
                        <input
                            type="text"
                            className="portal-filter-bar__search"
                            placeholder="Search name, email, phone, services, notes…"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>

                    <div className="portal-filter-bar__group">
                        <span className="portal-filter-bar__label">Status</span>
                        <select
                            className="portal-filter-bar__select"
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                        >
                            {STATUS_FILTER_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="portal-filter-bar__group">
                        <span className="portal-filter-bar__label">Type of Podcast</span>
                        <select
                            className="portal-filter-bar__select"
                            value={podcastTypeFilter}
                            onChange={(e) => setPodcastTypeFilter(e.target.value)}
                        >
                            <option value="">All</option>
                            {PODCAST_TYPE_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="portal-filter-bar__group">
                        <span className="portal-filter-bar__label">Frequency</span>
                        <select
                            className="portal-filter-bar__select"
                            value={frequencyFilter}
                            onChange={(e) => setFrequencyFilter(e.target.value)}
                        >
                            <option value="">All</option>
                            {FREQUENCY_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="portal-filter-bar__group">
                        <span className="portal-filter-bar__label">Has Podcast</span>
                        <select
                            className="portal-filter-bar__select"
                            value={alreadyHasPodcastFilter}
                            onChange={(e) => setAlreadyHasPodcastFilter(e.target.value)}
                        >
                            <option value="">All</option>
                            <option value="1">Yes</option>
                            <option value="0">No</option>
                        </select>
                    </div>
                </div>

                {error && <div className="alert alert--error">{error}</div>}
                {loading && <p>Loading leads…</p>}
                {!loading && !error && leads.length === 0 && <p>No leads found.</p>}

                {!loading && !error && leads.length > 0 && (
                    <div className="episode-list">
                        {leads.map((lead) => (
                            <div
                                key={lead.id}
                                className="episode-card"
                                style={{ borderLeftColor: "#2563eb" }}
                                onClick={() => openEditModal(lead)}
                            >
                                <div className="episode-card__main">
                                    <h3 className="episode-card__title">{lead.name}</h3>
                                    <div className="episode-card__podcast">
                                        {lead.email || "No email"} • {lead.phone || "No phone"}
                                    </div>
                                    <div className="episode-card__meta">
                                        Date: {formatLeadDate(lead.lead_date)} • Type:{" "}
                                        {formatPodcastType(lead.podcast_type)} • Frequency:{" "}
                                        {formatFrequency(lead.frequency)} • Has podcast:{" "}
                                        {lead.already_has_podcast ? "Yes" : "No"}
                                    </div>
                                </div>
                                <div className="episode-card__right">
                                    <span
                                        className={`pill ${getLeadStatusPillClass(lead.status)}`}
                                    >
                                        {formatStatus(lead.status)}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {modalOpen && (
                <div className="modal-overlay">
                    <div className="modal modal--wide">
                        <div className="modal__header">
                            <div>
                                <h3 className="modal__title">
                                    {form.id ? "Edit Lead" : "Create Lead"}
                                </h3>
                                <p className="modal__subtitle">
                                    Manage enquiry details and current status.
                                </p>
                            </div>
                            <button className="modal__close-btn" onClick={requestCloseModal}>
                                ✕
                            </button>
                        </div>

                        {formError && <div className="alert alert--error">{formError}</div>}

                        <form className="modal__body" onSubmit={handleSave} ref={formRef}>
                            <div className="modal__grid modal__grid--two-columns">
                                <div className="modal__column">
                                    <fieldset className="fieldset">
                                        <legend className="fieldset__legend">
                                            Lead Information
                                        </legend>

                                        <label className="form-field">
                                            <span className="form-field__label">Name</span>
                                            <input
                                                className="form-field__input"
                                                value={form.name}
                                                required
                                                onChange={(e) =>
                                                    setForm((prev) => ({
                                                        ...prev,
                                                        name: e.target.value,
                                                    }))
                                                }
                                            />
                                        </label>

                                        <label className="form-field">
                                            <span className="form-field__label">
                                                Email Address
                                            </span>
                                            <input
                                                type="email"
                                                className="form-field__input"
                                                value={form.email}
                                                onChange={(e) =>
                                                    setForm((prev) => ({
                                                        ...prev,
                                                        email: e.target.value,
                                                    }))
                                                }
                                            />
                                        </label>

                                        <label className="form-field">
                                            <span className="form-field__label">
                                                Phone Number
                                            </span>
                                            <input
                                                className="form-field__input"
                                                value={form.phone}
                                                onChange={(e) =>
                                                    setForm((prev) => ({
                                                        ...prev,
                                                        phone: e.target.value,
                                                    }))
                                                }
                                            />
                                        </label>

                                        <label className="form-field">
                                            <span className="form-field__label">Date</span>
                                            <input
                                                type="date"
                                                className="form-field__input"
                                                value={form.lead_date}
                                                onChange={(e) =>
                                                    setForm((prev) => ({
                                                        ...prev,
                                                        lead_date: e.target.value,
                                                    }))
                                                }
                                            />
                                        </label>

                                        <label className="form-field">
                                            <span className="form-field__label">
                                                Type of Podcast
                                            </span>
                                            <select
                                                className="form-field__select"
                                                value={form.podcast_type}
                                                onChange={(e) =>
                                                    setForm((prev) => ({
                                                        ...prev,
                                                        podcast_type: e.target.value,
                                                    }))
                                                }
                                            >
                                                <option value="">— Select —</option>
                                                {PODCAST_TYPE_OPTIONS.map((option) => (
                                                    <option key={option.value} value={option.value}>
                                                        {option.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </label>

                                        <label className="form-field">
                                            <span className="form-field__label">Frequency</span>
                                            <select
                                                className="form-field__select"
                                                value={form.frequency}
                                                onChange={(e) =>
                                                    setForm((prev) => ({
                                                        ...prev,
                                                        frequency: e.target.value,
                                                    }))
                                                }
                                            >
                                                <option value="">— Select —</option>
                                                {FREQUENCY_OPTIONS.map((option) => (
                                                    <option key={option.value} value={option.value}>
                                                        {option.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </label>
                                    </fieldset>
                                </div>

                                <div className="modal__column">
                                    <fieldset className="fieldset">
                                        <legend className="fieldset__legend">
                                            Sales Tracking
                                        </legend>

                                        <label className="form-field">
                                            <span className="form-field__label">Status</span>
                                            <select
                                                className="form-field__select"
                                                value={form.status}
                                                onChange={(e) =>
                                                    setForm((prev) => ({
                                                        ...prev,
                                                        status: e.target.value,
                                                    }))
                                                }
                                            >
                                                {STATUS_OPTIONS.map((option) => (
                                                    <option key={option.value} value={option.value}>
                                                        {option.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </label>

                                        <label className="form-field">
                                            <span className="form-field__label">
                                                Direct Debit Mandate (URL)
                                            </span>
                                            <input
                                                type="url"
                                                className="form-field__input"
                                                value={form.direct_debit_mandate_url}
                                                onChange={(e) =>
                                                    setForm((prev) => ({
                                                        ...prev,
                                                        direct_debit_mandate_url: e.target.value,
                                                    }))
                                                }
                                            />
                                        </label>

                                        {form.status === "signed_up" && (
                                            <label className="form-field">
                                                <span className="form-field__label">
                                                    Onboarding Link
                                                </span>
                                                <div
                                                    style={{
                                                        display: "flex",
                                                        gap: "8px",
                                                        alignItems: "center",
                                                    }}
                                                >
                                                    <input
                                                        className="form-field__input"
                                                        readOnly
                                                        value={
                                                            form.onboarding_token
                                                                ? `${window.location.origin}/#/onboarding?token=${form.onboarding_token}`
                                                                : "Save as Signed Up to generate link"
                                                        }
                                                    />
                                                    <button
                                                        type="button"
                                                        className="btn btn--secondary btn--small"
                                                        onClick={copyOnboardingLink}
                                                        disabled={!form.onboarding_token}
                                                    >
                                                        {copiedLink ? "Copied" : "Copy"}
                                                    </button>
                                                </div>
                                                {form.onboarding_completed_at && (
                                                    <span className="form-field__label">
                                                        Submitted: {String(form.onboarding_completed_at)}
                                                    </span>
                                                )}
                                            </label>
                                        )}

                                        {!!form.discovery_call_token && (
                                            <label className="form-field">
                                                <span className="form-field__label">
                                                    Discovery Call Link
                                                </span>
                                                <div
                                                    style={{
                                                        display: "flex",
                                                        gap: "8px",
                                                        alignItems: "center",
                                                    }}
                                                >
                                                    <input
                                                        className="form-field__input"
                                                        readOnly
                                                        value={`${window.location.origin}/#/discovery-call?token=${form.discovery_call_token}`}
                                                    />
                                                    <button
                                                        type="button"
                                                        className="btn btn--secondary btn--small"
                                                        onClick={copyDiscoveryLink}
                                                    >
                                                        {discoveryCopiedLink ? "Copied" : "Copy"}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="btn btn--secondary btn--small"
                                                        onClick={handleResendDiscoveryInvite}
                                                    >
                                                        Resend Invite
                                                    </button>
                                                </div>
                                                {form.discovery_call_scheduled_at && (
                                                    <span className="form-field__label">
                                                        Scheduled: {String(form.discovery_call_scheduled_at)}
                                                    </span>
                                                )}
                                            </label>
                                        )}

                                        <label className="form-field">
                                            <span className="form-field__label">
                                                Services
                                            </span>
                                            <textarea
                                                className="form-field__textarea"
                                                rows={5}
                                                value={form.services}
                                                onChange={(e) =>
                                                    setForm((prev) => ({
                                                        ...prev,
                                                        services: e.target.value,
                                                    }))
                                                }
                                            />
                                        </label>

                                        <label className="form-field">
                                            <span className="form-field__label">Notes</span>
                                            <textarea
                                                className="form-field__textarea"
                                                rows={4}
                                                value={form.notes}
                                                onChange={(e) =>
                                                    setForm((prev) => ({
                                                        ...prev,
                                                        notes: e.target.value,
                                                    }))
                                                }
                                            />
                                        </label>

                                        <label className="form-field__checkbox-row">
                                            <input
                                                type="checkbox"
                                                checked={Boolean(form.already_has_podcast)}
                                                onChange={(e) =>
                                                    setForm((prev) => ({
                                                        ...prev,
                                                        already_has_podcast: e.target.checked,
                                                    }))
                                                }
                                            />
                                            <span>Already have a podcast</span>
                                        </label>
                                    </fieldset>
                                </div>
                            </div>

                            <div className="modal__footer">
                                {form.id ? (
                                    <button
                                        type="button"
                                        className="btn btn--danger"
                                        disabled={deleting}
                                        onClick={handleDelete}
                                    >
                                        {deleting ? "Deleting..." : "Delete"}
                                    </button>
                                ) : (
                                    <span />
                                )}

                                <div style={{ display: "flex", gap: "10px" }}>
                                    <button
                                        type="button"
                                        className="btn btn--secondary"
                                        onClick={requestCloseModal}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="btn btn--primary"
                                        disabled={saving}
                                    >
                                        {saving ? "Saving..." : "Save Lead"}
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {isSettingsModalOpen && (
                <div className="modal-overlay">
                    <div className="modal">
                        <div className="modal__header">
                            <div>
                                <h3 className="modal__title">Leads Settings</h3>
                                <p className="modal__subtitle">
                                    Configure discovery call booking availability.
                                </p>
                            </div>
                            <button
                                className="modal__close-btn"
                                onClick={() => setIsSettingsModalOpen(false)}
                            >
                                ✕
                            </button>
                        </div>

                        {settingsError && (
                            <div className="alert alert--error">{settingsError}</div>
                        )}

                        {settingsLoading ? (
                            <div className="modal__body">
                                <p>Loading settings…</p>
                            </div>
                        ) : (
                            <form className="modal__body" onSubmit={handleSaveSettings}>
                                <fieldset className="fieldset">
                                    <legend className="fieldset__legend">Availability</legend>

                                    <label className="form-field">
                                        <span className="form-field__label">Timezone</span>
                                        <input
                                            className="form-field__input"
                                            value={settingsForm.timezone}
                                            onChange={(e) =>
                                                setSettingsForm((prev) => ({
                                                    ...prev,
                                                    timezone: e.target.value,
                                                }))
                                            }
                                        />
                                    </label>

                                    <label className="form-field">
                                        <span className="form-field__label">
                                            Outlook mailbox/user
                                        </span>
                                        <div
                                            style={{
                                                display: "flex",
                                                gap: 8,
                                                alignItems: "center",
                                            }}
                                        >
                                            <input
                                                className="form-field__input"
                                                value={
                                                    settingsForm.outlook_calendar_user_id || ""
                                                }
                                                onChange={(e) =>
                                                    setSettingsForm((prev) => ({
                                                        ...prev,
                                                        outlook_calendar_user_id:
                                                            e.target.value,
                                                    }))
                                                }
                                                placeholder="mailbox@yourdomain.com"
                                            />
                                            <button
                                                type="button"
                                                className="btn btn--secondary"
                                                onClick={handleLoadCalendars}
                                                disabled={calendarsLoading}
                                            >
                                                {calendarsLoading
                                                    ? "Loading…"
                                                    : "Load calendars"}
                                            </button>
                                        </div>
                                    </label>

                                    <label className="form-field">
                                        <span className="form-field__label">
                                            Outlook calendar
                                        </span>
                                        <select
                                            className="form-field__select"
                                            value={settingsForm.outlook_calendar_id || ""}
                                            onChange={(e) =>
                                                setSettingsForm((prev) => ({
                                                    ...prev,
                                                    outlook_calendar_id: e.target.value,
                                                }))
                                            }
                                        >
                                            <option value="">Default calendar</option>
                                            {calendarOptions.map((cal) => (
                                                <option key={cal.id} value={cal.id}>
                                                    {cal.name}
                                                </option>
                                            ))}
                                        </select>
                                    </label>

                                    <label className="form-field">
                                        <span className="form-field__label">
                                            Booking confirmation redirect URL
                                        </span>
                                        <input
                                            className="form-field__input"
                                            value={settingsForm.booking_redirect_url || ""}
                                            onChange={(e) =>
                                                setSettingsForm((prev) => ({
                                                    ...prev,
                                                    booking_redirect_url: e.target.value,
                                                }))
                                            }
                                            placeholder="https://example.com/thank-you"
                                        />
                                    </label>

                                    <label
                                        className="form-field"
                                        style={{
                                            display: "flex",
                                            flexDirection: "row",
                                            gap: 10,
                                            alignItems: "center",
                                        }}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={Boolean(
                                                settingsForm.booking_redirect_include_params
                                            )}
                                            onChange={(e) =>
                                                setSettingsForm((prev) => ({
                                                    ...prev,
                                                    booking_redirect_include_params:
                                                        e.target.checked,
                                                }))
                                            }
                                            style={{ margin: 0, flex: "0 0 auto" }}
                                        />
                                        <span className="form-field__label" style={{ margin: 0 }}>
                                            Include query params (`?email=...&name=...`) on
                                            booking redirect
                                        </span>
                                    </label>

                                    <div className="form-field">
                                        <span className="form-field__label">
                                            Available weekdays
                                        </span>
                                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                                            {[
                                                { day: 1, label: "Mon" },
                                                { day: 2, label: "Tue" },
                                                { day: 3, label: "Wed" },
                                                { day: 4, label: "Thu" },
                                                { day: 5, label: "Fri" },
                                                { day: 6, label: "Sat" },
                                                { day: 0, label: "Sun" },
                                            ].map((opt) => (
                                                <button
                                                    key={opt.day}
                                                    type="button"
                                                    className={
                                                        "btn " +
                                                        (settingsForm.available_weekdays?.includes(
                                                            opt.day
                                                        )
                                                            ? "btn--primary"
                                                            : "btn--secondary")
                                                    }
                                                    onClick={() => toggleWeekday(opt.day)}
                                                >
                                                    {opt.label}
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    <div style={{ display: "flex", gap: 10 }}>
                                        <label className="form-field" style={{ flex: 1 }}>
                                            <span className="form-field__label">Start time</span>
                                            <input
                                                type="time"
                                                className="form-field__input"
                                                value={settingsForm.day_start_time}
                                                onChange={(e) =>
                                                    setSettingsForm((prev) => ({
                                                        ...prev,
                                                        day_start_time: e.target.value,
                                                    }))
                                                }
                                            />
                                        </label>
                                        <label className="form-field" style={{ flex: 1 }}>
                                            <span className="form-field__label">End time</span>
                                            <input
                                                type="time"
                                                className="form-field__input"
                                                value={settingsForm.day_end_time}
                                                onChange={(e) =>
                                                    setSettingsForm((prev) => ({
                                                        ...prev,
                                                        day_end_time: e.target.value,
                                                    }))
                                                }
                                            />
                                        </label>
                                    </div>

                                    <div style={{ display: "flex", gap: 10 }}>
                                        <label className="form-field" style={{ flex: 1 }}>
                                            <span className="form-field__label">Slot minutes</span>
                                            <select
                                                className="form-field__select"
                                                value={settingsForm.slot_minutes}
                                                onChange={(e) =>
                                                    setSettingsForm((prev) => ({
                                                        ...prev,
                                                        slot_minutes: Number(e.target.value),
                                                    }))
                                                }
                                            >
                                                <option value={15}>15</option>
                                                <option value={30}>30</option>
                                                <option value={60}>60</option>
                                            </select>
                                        </label>
                                        <label className="form-field" style={{ flex: 1 }}>
                                            <span className="form-field__label">
                                                Buffer (minutes)
                                            </span>
                                            <input
                                                type="number"
                                                min={0}
                                                max={120}
                                                className="form-field__input"
                                                value={settingsForm.buffer_minutes}
                                                onChange={(e) =>
                                                    setSettingsForm((prev) => ({
                                                        ...prev,
                                                        buffer_minutes: Number(e.target.value),
                                                    }))
                                                }
                                            />
                                        </label>
                                    </div>

                                    <div style={{ display: "flex", gap: 10 }}>
                                        <label className="form-field" style={{ flex: 1 }}>
                                            <span className="form-field__label">Max calls/day</span>
                                            <input
                                                type="number"
                                                className="form-field__input"
                                                value={settingsForm.max_calls_per_day}
                                                onChange={(e) =>
                                                    setSettingsForm((prev) => ({
                                                        ...prev,
                                                        max_calls_per_day: Number(e.target.value),
                                                    }))
                                                }
                                            />
                                        </label>
                                    </div>

                                    <div style={{ display: "flex", gap: 10 }}>
                                        <label className="form-field" style={{ flex: 1 }}>
                                            <span className="form-field__label">
                                                Min notice (hours)
                                            </span>
                                            <input
                                                type="number"
                                                className="form-field__input"
                                                value={settingsForm.min_notice_hours}
                                                onChange={(e) =>
                                                    setSettingsForm((prev) => ({
                                                        ...prev,
                                                        min_notice_hours: Number(e.target.value),
                                                    }))
                                                }
                                            />
                                        </label>
                                        <label className="form-field" style={{ flex: 1 }}>
                                            <span className="form-field__label">
                                                Max days ahead
                                            </span>
                                            <input
                                                type="number"
                                                className="form-field__input"
                                                value={settingsForm.max_days_ahead}
                                                onChange={(e) =>
                                                    setSettingsForm((prev) => ({
                                                        ...prev,
                                                        max_days_ahead: Number(e.target.value),
                                                    }))
                                                }
                                            />
                                        </label>
                                    </div>
                                </fieldset>

                                <div className="modal__footer">
                                    <span />
                                    <div style={{ display: "flex", gap: 10 }}>
                                        <button
                                            type="button"
                                            className="btn btn--secondary"
                                            onClick={() => setIsSettingsModalOpen(false)}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            className="btn btn--primary"
                                            disabled={settingsSaving}
                                        >
                                            {settingsSaving ? "Saving..." : "Save Settings"}
                                        </button>
                                    </div>
                                </div>
                            </form>
                        )}
                    </div>
                </div>
            )}
        </PageShell>
    );
}

export default Leads;
